import { unmountComponentAtNode } from 'react-dom';
import { matchPath } from 'react-router';
import { LOCATION_CHANGE, CALL_HISTORY_METHOD } from 'connected-react-router';

import Analytics from '@hh.ru/analytics-js';
import { Debug, urlParser } from '@hh.ru/browser-api-utils';
import { RProgressApi } from '@hh.ru/rprogress';

import SPALink from './SPALink';
import { hasInjectData, inject, extract, hasActionsCache } from './injector';
import scrollCorrection from './scrollCorrection';
import urlHelper from './url';
import usePush from './usePush';
import useReplace from './useReplace';

export const HHTM_SOURCE_HEADER = 'X-hhtmSource';
export const HHTM_FROM_HEADER = 'X-hhtmFrom';
export const HHTM_FROM_LABEL_HEADER = 'X-hhtmFromLabel';

export { SPALink, usePush, useReplace, urlHelper };

const isCanceledFetch = (error) => error.message === 'canceled';
const isRedirectedRequest = (error) => error?.message === 'request_redirected';

const Features = {
    enableAlternativeHHTMParams: 'enable_alternative_hhtm_params',
};

const processRouteNotExist = (reactRoot, url, needDesyroy, isHistoryBack) => {
    if (reactRoot && needDesyroy && isHistoryBack) {
        unmountComponentAtNode(reactRoot);
        return;
    }

    window.location.assign(url);
};

const makeSPAMiddleware = ({
    fetcher,
    routes,
    headers,
    rootReducer,
    makeSetStoreField,
    setFieldDataAsIs,
    showDefaultError,
    resetSelectors,
    globalVars = window.globalVars,
    reactRoot,
    onSuccess,
    onError,
}) => {
    let additionalQueryParams = {};
    let forceHhtmForExternalLink = false;
    let forceHhtmForInternalLink = false;

    let cancelFetchingData = false;
    let cancelScrollCorrection = false;
    let isRedirect = false;

    const fetchingDataAction = makeSetStoreField('fetchingData');

    const isCanceledFetchingData = (action) => action?.payload?.args?.[2]?.cancelFetchingData;

    const getRoute = (url) => {
        for (const route of routes) {
            const { path, exact = true, strict } = route;
            const match = matchPath(url, { path, exact, strict: !!strict });
            if (match) {
                return { ...route, match };
            }
        }
        return undefined;
    };

    const injectData = ({ getState, result, action, next, urlWasChanged }) => {
        try {
            inject(
                fetchingDataAction,
                setFieldDataAsIs,
                result,
                {
                    noTrl: getState().router?.location?.query?.notrl,
                },
                resetSelectors,
                rootReducer,
                globalVars
            );
            next(urlWasChanged ? extract(action) : action);
        } catch (error) {
            Debug.log('error out', error);
            throw error;
        }
    };

    const processError = (dispatch, error, href) => {
        cancelScrollCorrection = true;
        dispatch(showDefaultError(error));

        if (!isCanceledFetch(error)) {
            window.location.assign(href);
        }
    };

    const fetchPageData = ({
        action,
        next,
        parsedUrl,
        hhtmFrom,
        hhtmFromLabel,
        getState,
        dispatch,
        preload,
        urlWasChanged,
    }) => {
        RProgressApi.start();
        dispatch(fetchingDataAction(true));
        return Promise.all([
            fetcher.getPage(
                parsedUrl.pathname,
                {
                    params: parsedUrl.params,
                    headers: {
                        ...headers,
                        [HHTM_SOURCE_HEADER]: '',
                        [HHTM_FROM_HEADER]: (!forceHhtmForInternalLink && hhtmFrom) || '',
                        [HHTM_FROM_LABEL_HEADER]: (!forceHhtmForInternalLink && hhtmFromLabel) || '',
                    },
                },
                true
            ),
            preload,
        ]).then(
            ([result]) => {
                RProgressApi.complete();
                injectData({ getState, result, action, next, urlWasChanged });
                onSuccess?.();

                return result;
            },
            (error) => {
                if ([403, 406].includes(error?.response?.status) || isRedirectedRequest(error)) {
                    // Для редиректа нужно поставить hhtm-параметры обратно в ссылку
                    if (!forceHhtmForInternalLink) {
                        parsedUrl.params.hhtmFrom = hhtmFrom;
                        parsedUrl.params.hhtmFromLabel = hhtmFromLabel;
                    }
                    isRedirect = true;
                    onError?.({ isRedirected: true, isCancelled: false, error });
                    window.location.assign(parsedUrl.href);
                    return undefined;
                }

                if (!isCanceledFetch(error)) {
                    RProgressApi.complete();
                }

                if (error?.response?.status < 500 && hasInjectData(rootReducer, error.response.data)) {
                    injectData({ getState, result: error.response.data, action, next, urlWasChanged });
                    onSuccess?.();
                    return error.response.data;
                }

                onError?.({ isRedirected: false, isCancelled: isCanceledFetch(error), error });
                return processError(dispatch, error, parsedUrl.href);
            }
        );
    };

    const getParsedUrlAndHhtm = (url) => {
        const parsedUrl = urlParser(url);

        parsedUrl.params = Object.assign(parsedUrl.params, additionalQueryParams);

        const hhtmFromUrl = parsedUrl.params.hhtmFrom;
        const hhtmSource = globalVars?.analyticsParams?.hhtmSource;
        const hhtmFrom = [].concat(hhtmFromUrl)[0] || hhtmSource;
        parsedUrl.params.hhtmFrom = hhtmFrom;

        const hhtmFromLabelUrl = parsedUrl.params.hhtmFromLabel;
        const hhtmFromLabel = [].concat(hhtmFromLabelUrl)[0];
        parsedUrl.params.hhtmFromLabel = hhtmFromLabel;

        delete parsedUrl.params.hhtmSource;

        return [parsedUrl, hhtmFrom, hhtmFromLabel];
    };

    const checkForceReloadIsNeeded = (getState, route) => {
        const currentRoute = getRoute(getState().router.location?.pathname);
        return !route || route.forceReload || !currentRoute || currentRoute?.forceReload;
    };

    return ({ dispatch, getState }) =>
        (next) =>
        (action) => {
            if (action.type === LOCATION_CHANGE) {
                cancelScrollCorrection = !!action.payload?.location?.state?.cancelScrollCorrection;
            }

            if (action.type === CALL_HISTORY_METHOD) {
                additionalQueryParams = action?.payload?.args?.[1]?.additionalQueryParams || {};
                forceHhtmForExternalLink = !!action?.payload?.args?.[1]?.forceHhtmForExternalLink;
                forceHhtmForInternalLink = !!action?.payload?.args?.[1]?.forceHhtmForInternalLink;
                // tempexp_31848_start
                const isAlternativeHHTMParamsEnabled = getState().features[Features.enableAlternativeHHTMParams];
                const xsrfToken = getState().xsrfToken;
                // tempexp_31848_end
                // Тут может быть объект Location
                let url = action.payload.args[0] && String(action.payload.args[0]);
                if (url && url.startsWith && url.startsWith('?')) {
                    url = getState().router.location.pathname + url;
                }
                const [parsedUrl, hhtmFrom, hhtmFromLabel] = getParsedUrlAndHhtm(url);
                const nextRoute = getRoute(parsedUrl.pathname);

                // tempexp_31848_start
                if (isAlternativeHHTMParamsEnabled) {
                    Analytics.sendHHEventToProxyButtonClick?.(xsrfToken, 'start_transition', {
                        hhtmFrom,
                        hhtmFromLabel,
                        event: 'start_transition',
                        path: parsedUrl.pathname + parsedUrl.search,
                    });
                }
                // tempexp_31848_end

                if (isCanceledFetchingData(action)) {
                    const currentRoute = getRoute(getState().router.location?.pathname);

                    /* Если текущий роутинг, отличается от следующего, но запроса за данными мы не делаем.
                     * Значит в коде возник race condition. Такое поведение ошибочно, потому как в стейте не будет данных
                     * для новой страницы */
                    if (currentRoute?.match?.path === nextRoute?.match?.path) {
                        cancelFetchingData = true;
                        return next(action);
                    }
                    return undefined;
                }

                // Нужно смотреть именно на origin, потому что request.host
                // неправильно работает на hh.ru.STAND.pyn.ru (значение остаётся hh.ru).
                // А в request.origin приходит https://hh.ru.STAND.pyn.ru
                const origin = getState().request?.origin || null;

                const isExternalUrl = !urlHelper.isInternal(url, origin);
                const isForceReload = checkForceReloadIsNeeded(getState, nextRoute);

                if (isExternalUrl) {
                    if (!forceHhtmForExternalLink) {
                        delete parsedUrl.params.hhtmFrom;
                        delete parsedUrl.params.hhtmFromLabel;
                    }
                    processRouteNotExist(reactRoot, parsedUrl.href, getState().hasDestroyIfAppRouteNotExist);
                    return undefined;
                }

                if (isForceReload) {
                    processRouteNotExist(reactRoot, parsedUrl.href, getState().hasDestroyIfAppRouteNotExist);
                    return undefined;
                }

                if (!forceHhtmForInternalLink) {
                    delete parsedUrl.params.hhtmFrom;
                    delete parsedUrl.params.hhtmFromLabel;
                }

                action.payload.args[0] = parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;

                fetchPageData({
                    action,
                    next,
                    parsedUrl,
                    hhtmFrom,
                    hhtmFromLabel,
                    dispatch,
                    getState,
                    preload: nextRoute.component.preload(),
                }).finally(() => {
                    if (!cancelScrollCorrection && !isRedirect) {
                        // важно получить location после выполнения fetchPageData, иначе он будет старый
                        scrollCorrection(getState().router.location && getState().router.location.hash, false);
                    }
                });

                return undefined;
            }

            /*
             * При навигации «вперед» и «назад» вызывается только LOCATION_CHANGE event
             * Так происходит потому что react-router-redux работает следующим образом:
             * 1) CALL_HISTORY_METHOD описывает какой метод у объекта history нужно вызывать;
             * 2) Этот action летит в middleware;
             * 3) В middleware этот метод вызывается (например history.push(someUrl)).
             *
             * В случае с «вперед» и «назад» history вызывается «не нами», поэтому есть только один action - LOCATION_CHANGE
             * Для простоты кода мы не вводим дополнительных условий на модель инжекта данных.
             *
             * Поэтому в случае с forward/backward у нас в store летит: LOCATION_CHANGE, [батч данных]
             * А в случае с обычной ссылкой: CALL_HISTORY_METHOD, [батч данных + LOCATION_CHANGE]
             */
            if (
                action.type === LOCATION_CHANGE &&
                !action.payload.isFirstRendering &&
                !hasActionsCache() &&
                !cancelFetchingData
            ) {
                const { pathname, search, hash } = action.payload.location;

                const nextRoute = getRoute(pathname);
                const currentRoute = getRoute(getState().router.location?.pathname);

                if (
                    currentRoute?.isProxyService &&
                    nextRoute?.isProxyService &&
                    currentRoute?.match?.path === nextRoute?.match?.path
                ) {
                    return next(action);
                }

                const [parsedUrl, hhtmFrom, hhtmFromLabel] = getParsedUrlAndHhtm(`${pathname}${search}${hash}`);

                const isForceReload = checkForceReloadIsNeeded(getState, nextRoute);

                if (isForceReload) {
                    const { pathname: statePathname, search: stateSearch } = getState().router.location;
                    const isSamePage = pathname === statePathname && search === stateSearch;

                    if (!isSamePage) {
                        processRouteNotExist(reactRoot, parsedUrl.href, getState().hasDestroyIfAppRouteNotExist, true);
                    }
                    return undefined;
                }

                fetchPageData({
                    action,
                    next,
                    parsedUrl,
                    hhtmFrom,
                    hhtmFromLabel,
                    dispatch,
                    getState,
                    preload: nextRoute.component.preload(),
                    urlWasChanged: true,
                }).finally(() => {
                    if (!cancelScrollCorrection) {
                        scrollCorrection(getState().router.location.hash, true);
                    }
                });

                return undefined;
            }

            if (action.type === LOCATION_CHANGE && !action.payload.isFirstRendering && cancelFetchingData) {
                cancelFetchingData = false;
            }

            return next(extract(action));
        };
};

export default makeSPAMiddleware;
